import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings } from "lucide-react";
import { useI18n } from "../i18n";
import { cookieBannerCopy } from "../data/legalContent";

const STORAGE_KEY = "nida_cookie_consent";

const defaultPrefs = { essential: true, analytics: false, marketing: false };

function readConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        return { ...defaultPrefs, ...obj };
    } catch {
        return null;
    }
}

function writeConsent(prefs) {
    localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...prefs, essential: true, _at: new Date().toISOString() })
    );
}

export default function CookieBanner() {
    const { lang } = useI18n();
    const c = cookieBannerCopy[lang];
    const [open, setOpen] = useState(false);
    const [showCustomize, setShowCustomize] = useState(false);
    const [prefs, setPrefs] = useState(defaultPrefs);

    useEffect(() => {
        const existing = readConsent();
        if (!existing) setOpen(true);
        else setPrefs(existing);
    }, []);

    useEffect(() => {
        const handler = () => {
            setPrefs(readConsent() || defaultPrefs);
            setOpen(true);
            setShowCustomize(true);
        };
        window.addEventListener("nida:open-cookie-prefs", handler);
        return () => window.removeEventListener("nida:open-cookie-prefs", handler);
    }, []);

    const acceptAll = () => {
        const next = { essential: true, analytics: true, marketing: true };
        writeConsent(next);
        setPrefs(next);
        setOpen(false);
        setShowCustomize(false);
    };

    const rejectOptional = () => {
        const next = { essential: true, analytics: false, marketing: false };
        writeConsent(next);
        setPrefs(next);
        setOpen(false);
        setShowCustomize(false);
    };

    const savePrefs = () => {
        writeConsent(prefs);
        setOpen(false);
        setShowCustomize(false);
    };

    if (!open) return null;

    return (
        <>
            <div
                data-testid="cookie-banner"
                role="dialog"
                aria-label="Cookies"
                className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6 pointer-events-none"
            >
                <div className="mx-auto max-w-3xl pointer-events-auto">
                    <div className="card-surface gradient-border rounded-2xl p-5 md:p-6 shadow-2xl shadow-black/50">
                        <div className="flex items-start gap-4">
                            <div className="hidden sm:inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-brand-glow">
                                <Cookie className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <h2 className="text-base md:text-lg font-semibold tracking-tight">
                                            {c.title}
                                        </h2>
                                        <p className="mt-1.5 text-xs md:text-sm text-white/65 leading-relaxed">
                                            {c.body}{" "}
                                            <Link
                                                to="/cookies"
                                                data-testid="cookie-banner-readmore"
                                                className="underline underline-offset-4 hover:text-white"
                                            >
                                                {c.readMore}
                                            </Link>
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setOpen(false)}
                                        aria-label="Close"
                                        className="text-white/40 hover:text-white text-xl leading-none -mt-1"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>

                                {showCustomize && (
                                    <div className="mt-4 space-y-2.5 border-t border-white/[0.06] pt-4">
                                        <ToggleRow
                                            title={c.cats.essential.title}
                                            desc={c.cats.essential.desc}
                                            checked={true}
                                            disabled
                                            testid="cookie-pref-essential"
                                        />
                                        <ToggleRow
                                            title={c.cats.analytics.title}
                                            desc={c.cats.analytics.desc}
                                            checked={prefs.analytics}
                                            onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                                            testid="cookie-pref-analytics"
                                        />
                                        <ToggleRow
                                            title={c.cats.marketing.title}
                                            desc={c.cats.marketing.desc}
                                            checked={prefs.marketing}
                                            onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                                            testid="cookie-pref-marketing"
                                        />
                                    </div>
                                )}

                                <div className="mt-5 flex flex-wrap gap-2">
                                    {!showCustomize && (
                                        <button
                                            onClick={() => setShowCustomize(true)}
                                            data-testid="cookie-customize"
                                            className="btn-ghost text-xs px-4 py-2"
                                        >
                                            <Settings className="h-3.5 w-3.5" />
                                            {c.customize}
                                        </button>
                                    )}
                                    <button
                                        onClick={rejectOptional}
                                        data-testid="cookie-reject"
                                        className="btn-ghost text-xs px-4 py-2"
                                    >
                                        {c.reject}
                                    </button>
                                    {showCustomize ? (
                                        <button
                                            onClick={savePrefs}
                                            data-testid="cookie-save"
                                            className="btn-primary text-xs px-4 py-2 ml-auto"
                                        >
                                            {c.save}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={acceptAll}
                                            data-testid="cookie-accept-all"
                                            className="btn-primary text-xs px-4 py-2 ml-auto"
                                        >
                                            {c.accept}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function ToggleRow({ title, desc, checked, onChange, disabled, testid }) {
    return (
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
                <p className="text-sm font-medium text-white">{title}</p>
                <p className="text-xs text-white/55">{desc}</p>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                data-testid={testid}
                onClick={() => !disabled && onChange?.(!checked)}
                className={`relative h-5 w-9 shrink-0 rounded-full transition ${
                    checked ? "bg-brand" : "bg-white/15"
                } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
                <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${
                        checked ? "left-[18px]" : "left-0.5"
                    }`}
                />
            </button>
        </div>
    );
}
